import {
  dialog,
  getCurrentWindow,
  Menu,
  isDevMode,
  remote,
  shell,
  logDirectoryPath,
  app
} from './electron-modules';
import {
  WorkflowExecutable,
  PythonExecutable
} from "./executable";
import {t} from './localization';

const menuStateCache = {} as any;

const fileMenu = {
  get label() {
    return t("electron-menu.file.header");
  },
  submenu: [
    {
      get label() {
        return t("electron-menu.file.open-workflow");
      },
      click: async () => {
        new WorkflowExecutable().run({command: []});
      }
    },
    {
      get label() {
        return t("electron-menu.file.global-settings");
      },
      click: async () => {
        sendToMainWindow('open-global-settings');
      }
    },
    {
      type: 'separator'
    },
    {
      get label() {
        return t("electron-menu.file.new-project");
      },
      click: async () => {
        sendToMainWindow("new-project");
      }
    },
    {
      get label() {
        return t("electron-menu.file.open-project");
      },
      click: async () => {
        sendToMainWindow("load-project");
      }
    },
    {
      get label() {
        return t("electron-menu.file.save-project");
      },
      click: async () => {
        sendToMainWindow("save-project");
      }
    },
    {
      get label() {
        return t("electron-menu.file.save-as");
      },
      click: async () => {
        sendToMainWindow("save-project-as");
      }
    },
    {
      type: 'separator'
    },
    {
      get label() {
        return t("electron-menu.file.exit-without-saving");
      },
      click: async () => {
        app.exit();
      }
    },
  ]
};

const helpMenu = {
  get label() {
    return t("electron-menu.help.header");
  },
  submenu: [
    {
      get label() {
        return t("electron-menu.help.about");
      },
      click: async () => {
        sendToMainWindow('open-about');
      },
    },
    {
      visible: isDevMode,
      accelerator: 'Shift+F12',
      get label() {
        return t("electron-menu.help.dev-tools");
      },
      click: () => {
        const mainWindow = remote.getCurrentWindow();
        mainWindow.toggleDevTools();
      },
    },
    {
      get label() {
        return t("electron-menu.help.open-logs");
      },
      click: async () => {
        shell.openPath(logDirectoryPath);
      }
    },
    {
      get label() {
        return t("electron-menu.help.clear-cache");
      },
      click: async () => {
        clearMlResources();
      }
    },
    {
      get label() {
        return t("electron-menu.help.support");
      },
      click: async () => {
        shell.openExternal('https://solv3d.com/support/');
      }
    },
  ]
};

const toolsMenu = {
  id: 'tools',
  get label() {
    return t("electron-menu.tools.header");
  },
  submenu: [
    {
      id: 'projections',
      get label() {
        return t("electron-menu.tools.projections.header");
      },
      submenu: [
        {
          id: 'projection-manager',
          get label() {
            return t("electron-menu.tools.projections.manager");
          },
          click: async () => {
            sendToMainWindow("open-projection-manager");
          }
        },
        {
          id: 'local-projections',
          get label() {
            return t("electron-menu.tools.projections.local");
          },
          click: async () => {
            sendToMainWindow("open-local-projection");
          }
        },
      ]
    },
    {
      id: "aligner-menu",
      get label() {
        return t("electron-menu.tools.data-aligner.header");
      },
      submenu: [
        {
          id: 'aligner-advanced-menu',
          enabled: false,
          get label() {
            return t("electron-menu.tools.data-aligner.advanced");
          },
          click: async () => {
            sendToMainWindow("open-image-aligner-adv");
          }
        },
        {
          id: 'aligner-basic-menu',
          enabled: false,
          get label() {
            return t("electron-menu.tools.data-aligner.basic");
          },
          click: async () => {
            sendToMainWindow("open-image-aligner-basic");
          }
        },
      ]
    },
    {
      id: "training-menu",
      get label() {
        return t("electron-menu.tools.training.header");
      },
      submenu: [
        {
          id: 'label-ortho-menu',
          enabled: false,
          get label() {
            return t("electron-menu.tools.training.ortho");
          },
          click: async () => {
            sendToMainWindow("open-label-ortho");
          }
        },
        {
          id: 'label-scene-menu',
          get label() {
            return t("electron-menu.tools.training.scene");
          },
          enabled: false,
          click: async () => {
            sendToMainWindow("open-label-scene");
          }
        },
      ]
    },
    {
      id: 'point-markup-menu',
      get label() {
        return t("electron-menu.tools.markup.header");
      },
      submenu: [
        {
          get label() {
            return t("electron-menu.tools.markup.modify");
          },
          click: async () => {
            sendToMainWindow("open-point-markup");
          }
        },
        {
          get label() {
            return t("electron-menu.tools.markup.split");
          },
          click: async () => {
            sendToMainWindow("open-data-splitting");
          }
        },
      ]
    },
    {
      id: 'exporter-menu',
      get label() {
        return t("electron-menu.tools.export");
      },
      enabled: false,
      click: async () => {
        sendToMainWindow("open-static-export");
      }
    }
  ]
};

const debugMenu = {
  get label() {
    return t("electron-menu.debug.refresh");
  },
  click: () => {
    getCurrentWindow().reload();
  }
};

const sendToMainWindow = (message, payload=null) => {
  const mainWindow = remote.getCurrentWindow();
  mainWindow.webContents.send(message, payload);
};

const clearMlResources = () => {
  let exe = new PythonExecutable();
  let commands = [
    "-p", "remove_ml_resources",
    "--remove", true
  ];

  exe.run({
    command: commands,
    saveLogs: true,
    onClose: () => {
      dialog.showMessageBox({
        type: 'info',
        title: t("dialog.clear-cache-title"),
        message: t("dialog.clear-cache-message"),
        noLink: true
      });
    }
  });
};

const setToolEnabled = (toolPath, state) => {
  let parent = Menu.getApplicationMenu().items;

  toolPath.forEach(toolName => {
    const element = parent.find(item => item.id === toolName);
    if (element.submenu) {
      parent = element.submenu.items;
    } else {
      element.enabled = state;
    }
  });
};

const setMenuState = async toolPath => {
  if (isDevMode) {
    setToolEnabled(toolPath, true);
    return;
  }

  const cacheKey = toolPath.join("-");

  if (cacheKey in menuStateCache) {
    const {running, state} = menuStateCache[cacheKey];
    if (running) return;

    // Apply saved menu state
    setToolEnabled(toolPath, state);
    return;
  }

  // Insert default cache values
  menuStateCache[cacheKey] = {
    running: true,
    state: null
  };

  const toolName = toolPath.slice(-1)[0];
  const exe = new PythonExecutable();
  exe.run({
    command: ['--check-tool', toolName],
    showStatus: false,
    onLine: jsonData => {
      if (!jsonData.license_check) {
        return;
      }

      let {valid} = jsonData.license_check;

      // Update cache values
      menuStateCache[cacheKey] = {
        running: false,
        state: valid
      };

      setToolEnabled(toolPath, valid);
    }
  });
};

const checkAllowedMenuItems = async () => {
  // TODO: check all tools at once
  setMenuState(['tools', 'aligner-menu', 'aligner-advanced-menu']);
  setMenuState(['tools', 'aligner-menu', 'aligner-basic-menu']);
  setMenuState(['tools', 'exporter-menu']);
  setMenuState(['tools', 'training-menu', 'label-ortho-menu']);
  setMenuState(['tools', 'training-menu', 'label-scene-menu']);
};

export const buildElectronMenu = () => {
  if (!Menu) return;

  const template = isDevMode
    ? [fileMenu, toolsMenu, helpMenu, debugMenu]
    : [fileMenu, toolsMenu, helpMenu];

  const menu = Menu.buildFromTemplate(template);
  Menu.setApplicationMenu(menu);
  checkAllowedMenuItems();
};
